import { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AdminToast, ToastProvider } from '../../ui/components';
import { useAPICall, useAppSelector } from '../../hooks';
import CampaignService from '../../sdk/services/campaignService';
import { DynamicObjectInterface } from '../../sdk/type';
import Error from '../../assets/image/icons/ico_error.svg';
import { dateTimeToCustomFormat } from '../../ui/utils';
import GameRouter from '../game/GameRouter';
import { AdminMenu } from './menus';
import styles from './Admin.module.scss';
import { AdminDashboard } from './admin-dashboard/admin-dashboard';
import { AdminUserEditPage, AdminUserList } from './user-setup';
// import { AdminKPIList, AdminKPIEditPage } from './kpi-setup';
import { AdminRewardEditPage, AdminRewardList } from './reward-setup';
import { AdminLocationList } from './location-setup';
import { AdminCoachList, AdminCoachEditPage } from './coach-setup';
import { AdminCampaignList, AdminCampaignEditPage } from './campaign-configuration';
import { AdminTeamsList, AdminTeamEditPage, AdminTeamCreatePage } from './teams';
import { AdminCoachEnrollmentList, AdminCoachEnrollmentEditPage } from './coach-enrollment-configuration';
import { AddParticipant } from './teams/edit/add-participant/[teamId]';
import { AdminParticipantDetails } from './teams/edit/participant-details/[partiId]';
import { AdminGeneralParticipantList } from './participants';
import { AdminGeneralCoachList } from './coaches';
import { AdminGeneralCoachDetails } from './coaches/edit/[coachId]';
import { AdminGeneralLeadersList } from './leaders/list';
import { AdminGeneralLeaderDetails } from './leaders/edit/[leaderId]';
import { AdminGeneralInventoryList } from './inventory';
import { AdminInventoryEditPage } from './inventory/edit/[recId]';
import { AdminGeneralRedemptionList } from './redemption';
import { AdminGeneralRedemptionEdit } from './redemption/edit/[recId]';
import { RequiredAuth } from './RequiredAuth';
import { BulkUpload } from './bulk-upload';
import { ProjectSponsor } from './backend-process';
import { Report } from './report';

interface filteredCampaignData {
  campaignName: string;
  dataRemovalDate: string;
}

export const AdminHome = () => {
  const adminType = useAppSelector((state) => Number(state.auth.userInfo?.adminType ?? 0));
  const [campaignData, setCampaignData] = useState<filteredCampaignData>();
  const apiCall = useAPICall();
  let location = useLocation();

  const isUnauthorized =
    (adminType === 2 &&
      (location.pathname.startsWith('/admin/promotion') ||
      location.pathname.startsWith('/admin/master'))) ||
    (adminType === 3 &&
      (location.pathname.startsWith('/admin/challenge') ||
      location.pathname.startsWith('/admin/master'))) ||
     (adminType === 3 &&
      (location.pathname.startsWith('/admin/promotion/report') ||
      location.pathname.startsWith('/admin/promotion/bulk-upload')));

  useEffect(() => {
    (async () => {
      const res = await apiCall(CampaignService?.getCampaigns);
      let campaignData = res?.data?.data ?? [];

      campaignData = campaignData?.filter(
        (i: DynamicObjectInterface) => i.status !== 'DELETED' && i.status !== 'CLOSED' && !!i.dataRemovalDate,
      );

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const filteredCampaignData = campaignData?.find((i: DynamicObjectInterface) => {
        const removalDate = new Date(i.dataRemovalDate);
        removalDate.setHours(0, 0, 0, 0);

        const notificationStart = new Date(removalDate.getTime() - 15 * 86400000); // 15 days before
        return today >= notificationStart && today < removalDate;
      });
      setCampaignData(filteredCampaignData);
    })();
  }, []);

  return (
    <RequiredAuth>
      <div className={styles['adminPortal']}>
        <ToastProvider>
          <AdminToast timeout={100000} topPosition="100px">
            <AdminMenu adminType={adminType} />
            {isUnauthorized ? (
              <div style={{ padding: 24 , width: '80%'  }}>
                <h1 style={{ color: 'red'  }} >401 – Permission Denied</h1>
                <p>You do not have access to this page.</p>
              </div>
            ): (
              <div className={styles['adminContent']}>
              {!!campaignData && !window.location.href.includes('edit') && (
                <div className={styles['adminPortal-notification']}>
                  <img src={Error} alt={`Warning`} width={'32px'} height={'32px'} className={styles['warning']} />
                  <div>
                    Note: All users' health data for {campaignData.campaignName} will be removed by{' '}
                    {dateTimeToCustomFormat(campaignData.dataRemovalDate, 'DD/MM/yyyy')}. Please download all required
                    data before this date.
                  </div>
                </div>
              )}

              <Routes>
                <Route path="/user-setup/edit" element={<AdminUserEditPage />} />
                <Route path="/user-setup/list" element={<AdminUserList />} />
                {/* <Route path="/kpi-setup/list" element={<AdminKPIList />} />
              <Route path="/kpi-setup/edit" element={<AdminKPIEditPage />} /> */}
                <Route path="/reward-setup/list" element={<AdminRewardList />} />
                <Route path="/reward-setup/edit" element={<AdminRewardEditPage />} />
                <Route path="/location-setup/list" element={<AdminLocationList />} />
                <Route path="/coach-setup/list" element={<AdminCoachList />} />
                <Route path="/coach-setup/edit" element={<AdminCoachEditPage />} />
                <Route path="/campaign-configuration/list" element={<AdminCampaignList />} />
                <Route path="/campaign-configuration/edit" element={<AdminCampaignEditPage />} />
                <Route path="/manage-team/list" element={<AdminTeamsList />} />
                <Route path="/manage-team/create-team" element={<AdminTeamCreatePage />} />
                <Route path="/manage-team/edit" element={<AdminTeamEditPage />} />
                <Route path="/manage-team/edit/add-participant" element={<AddParticipant />} />
                <Route path="/manage-team/edit/participant-details" element={<AdminParticipantDetails />} />
                <Route path="/coach-enrollment-configuration/list" element={<AdminCoachEnrollmentList />} />
                <Route path="/coach-enrollment-configuration/edit" element={<AdminCoachEnrollmentEditPage />} />
                <Route path="/coach-enrollment-configuration/edit" element={<AdminCoachEnrollmentEditPage />} />
                <Route path="/manage-participant/list" element={<AdminGeneralParticipantList />} />
                <Route path="/manage-participant/edit" element={<AdminParticipantDetails />} />
                <Route path="/manage-coach/list" element={<AdminGeneralCoachList />} />
                <Route path="/manage-coach/edit" element={<AdminGeneralCoachDetails />} />
                <Route path="/manage-leader/list" element={<AdminGeneralLeadersList />} />
                <Route path="/manage-leader/edit" element={<AdminGeneralLeaderDetails />} />
                <Route path="/promotion/manage-inventory/list" element={<AdminGeneralInventoryList adminType={adminType} />} />
                <Route path="/promotion/manage-inventory/edit" element={<AdminInventoryEditPage adminType={adminType}/>} />
                <Route path="/promotion/manage-redemption/list" element={<AdminGeneralRedemptionList />} />
                <Route path="/promotion/manage-redemption/edit" element={<AdminGeneralRedemptionEdit adminType={adminType} />} />
                <Route path="/promotion/manage-redemption/new" element={<GameRouter />} />
                <Route
                  path="/bulk-upload/coach-master"
                  element={<BulkUpload title="Coach Master" campaignType="CHALLENGE" name="coach-list" />}
                />
                {/* <Route
                  path="/bulk-upload/coach-enrollment-master"
                  element={<BulkUpload title="Coach Enrollment Master" name="coach-enrollment-master" />}
                />
                <Route path="/bulk-upload/team-list" element={<BulkUpload title="Team List" name="team-list" />} />
                <Route
                  path="/bulk-upload/check-abo"
                  element={<BulkUpload title="ABO Checking - Team List" name="check-abo" />}
                />
                <Route
                  path="/bulk-upload/check-abo-coach-enrollment"
                  element={<BulkUpload title="ABO Checking - Coach Enrollment" name="check-abo-coach-enrollment" />}
                /> 
                <Route
                  path="/bulk-upload/sponsored-abo-leader"
                  element={<BulkUpload title="Leader - Group Sponsor" name="sponsored-abo-leader" />}
                />
                <Route
                  path="/bulk-upload/sponsored-abo-coach"
                  element={<BulkUpload title="Coach - Group Sponsor" name="sponsored-abo-coach" />}
                />*/}
                <Route path="/report/team" element={<Report title="Report - Team" campaignType="CHALLENGE" name="team" />} />
                <Route
                  path="/report/coach-group"
                  element={<Report title="Report - Coach (Group)" campaignType="CHALLENGE" name="coach-group" />}
                />
                <Route
                  path="/report/coach-individual"
                  element={<Report title="Report - Coach (Individual)" name="coach-individual" />}
                />
                <Route path="/report/leader" element={<Report title="Report - Leader" name="leader" />} />
                <Route path="/promotion/report/inventory" element={<Report title="Report - Inventory" campaignType="PROMOTION" name="inventory" />} />
                <Route path="/promotion/report/redemption" element={<Report title="Report - Redemption" campaignType="PROMOTION" name="promo-redemption" />} />
                <Route
                    path="/promotion/bulk-upload/inventory"
                    element={<BulkUpload title="Inventory" campaignType="PROMOTION"  name="inventory-list" />}
                  />
                <Route
                  path="/backend-process/project-sponsor"
                  element={<ProjectSponsor title="Sponsored ABO" name="project-sponsor" />}
                />
                <Route path="/" element={<AdminDashboard />} />
              </Routes>
              </div>
            )}
          </AdminToast>
        </ToastProvider>
      </div>
    </RequiredAuth>
  );
};
